import { useQuery } from '@tanstack/react-query'
import { useAuth } from '@/hooks/useAuth'
import { useProtocol } from '@/hooks/useProtocol'
import { useMastodonLists } from '@/hooks/useMastodonLists'
import { useMastodonFollowedTags } from '@/hooks/useMastodonFollowedTags'
import { useMastodonTabPrefs } from '@/hooks/useMastodonTabPrefs'
import { usePlatformGuard } from '@/hooks/usePlatformGuard'
import { fetchSavedFeeds, fetchFeedGeneratorViews } from '@/services/feedsService'
import { fetchListByUri } from '@/services/listsService'
import type { ResolvedFeed } from '@/types/feeds'

const FOLLOWING_FEED: ResolvedFeed = { uri: 'following', displayName: 'Following' }

const PIXELFED_FEEDS: ResolvedFeed[] = [
    FOLLOWING_FEED,
    { uri: 'pixelfed:local', displayName: 'Local' },
    { uri: 'pixelfed:federated', displayName: 'Federated' },
]

export function useSavedFeeds() {
    const { session, mastodonSession } = useAuth()
    const { selectedProtocol } = useProtocol()

    const isFediverse = selectedProtocol === 'mastodon' || selectedProtocol === 'pixelfed'
    const isNostr = !usePlatformGuard('atproto', 'activitypub')

    // Always call useMastodonLists / useMastodonFollowedTags / useMastodonTabPrefs (hooks
    // must be called unconditionally). Data is only used when selectedProtocol === 'mastodon'.
    const {
        lists: mastodonLists,
        isLoading: mastodonListsLoading,
        hasSettled: mastodonListsSettled,
        isSuccess: mastodonListsSuccess,
    } = useMastodonLists()

    const {
        tags: mastodonFollowedTags,
        isLoading: mastodonTagsLoading,
        hasSettled: mastodonTagsSettled,
        isSuccess: mastodonTagsSuccess,
    } = useMastodonFollowedTags()

    const mastodonPrefs = useMastodonTabPrefs({
        lists: mastodonLists,
        isListsAuthoritative: mastodonListsSuccess,
        followedTags: mastodonFollowedTags,
        isFollowedTagsAuthoritative: mastodonTagsSuccess,
        session: mastodonSession,
    })

    const query = useQuery<ResolvedFeed[]>({
        queryKey: ['savedFeeds', session?.did ?? null],
        queryFn: async () => {
            const savedFeeds = await fetchSavedFeeds(session!)
            const feedGeneratorUris = savedFeeds.filter(f => f.type === 'feed').map(f => f.value)

            // fetchFeedGeneratorViews can fail (network error, API outage, expired session).
            // Catch and fall back to an empty list so the query doesn't fail entirely —
            // feed items without generator metadata are still shown with fallback display names.
            const generators =
                feedGeneratorUris.length > 0
                    ? await fetchFeedGeneratorViews(session!, feedGeneratorUris).catch(err => {
                          console.error('[useSavedFeeds] fetchFeedGeneratorViews failed', err)
                          return []
                      })
                    : []

            const generatorMap = new Map(generators.map(g => [g.uri, g]))

            const listUris = savedFeeds.filter(f => f.type === 'list').map(f => f.value)
            const listMetas = await Promise.all(
                listUris.map(uri => fetchListByUri(session!, uri).catch(() => null))
            )
            const listMetaMap = new Map(listUris.map((uri, i) => [uri, listMetas[i]]))

            // Build full list with id/pinned/type for the Feeds management page
            const resolved: ResolvedFeed[] = []
            for (const feed of savedFeeds) {
                if (feed.type === 'timeline' && feed.value === 'following') {
                    resolved.push({
                        uri: 'following',
                        displayName: 'Following',
                        id: feed.id,
                        type: 'timeline',
                        pinned: feed.pinned,
                        value: feed.value,
                    })
                } else if (feed.type === 'feed') {
                    // Use generator metadata when available; fall back to the raw URI so the
                    // feed still appears in the dropdown even if the generator metadata API fails.
                    const gen = generatorMap.get(feed.value)
                    resolved.push({
                        uri: gen?.uri ?? feed.value,
                        displayName: gen?.displayName ?? feed.value,
                        avatar: gen?.avatar,
                        id: feed.id,
                        type: 'feed',
                        pinned: feed.pinned,
                        value: feed.value,
                    })
                } else if (feed.type === 'list') {
                    const meta = listMetaMap.get(feed.value)
                    resolved.push({
                        uri: feed.value,
                        displayName: meta?.name ?? 'List',
                        avatar: meta?.avatar,
                        id: feed.id,
                        type: 'list',
                        pinned: feed.pinned,
                        value: feed.value,
                    })
                }
            }
            return resolved
        },
        enabled: !!session && !isFediverse && !isNostr,
        staleTime: 10 * 60 * 1000,
        gcTime: 30 * 60 * 1000,
    })

    if (isNostr) {
        return {
            feeds: [FOLLOWING_FEED],
            allFeeds: [] as ResolvedFeed[],
            isLoading: false,
            isReady: true,
        }
    }

    if (selectedProtocol === 'pixelfed') {
        return {
            feeds: PIXELFED_FEEDS,
            allFeeds: [] as ResolvedFeed[],
            isLoading: false,
            isReady: true,
        }
    }

    if (selectedProtocol === 'mastodon') {
        // Only propagate loading state when a Mastodon session actually exists.
        // Without a session the queries are disabled and isLoading would be a false
        // positive. Once BOTH the lists and followed-tags queries have settled (success
        // *or* error) this stays false forever: consumers gate a full-page skeleton on it,
        // and a post-error refetch (triggered by any newly-mounted observer of either
        // query) must not re-raise that gate — doing so unmounts the observer that raised
        // it and loops indefinitely (issue #2118).
        return {
            feeds: mastodonPrefs.visibleFeeds,
            allFeeds: mastodonPrefs.allFeeds,
            isLoading:
                !!mastodonSession &&
                ((mastodonListsLoading && !mastodonListsSettled) ||
                    (mastodonTagsLoading && !mastodonTagsSettled)),
            // Authority accompanies the feed result so consumers never treat a
            // pre-authoritative snapshot as final. Mirrors mobile's useSavedFeeds.isReady.
            // Cached successful data remains authoritative during background refetch errors
            // (React Query keeps isSuccess true), so this does not flap on a transient error.
            // Both the lists and followed-tags queries must be authoritative — see
            // useMastodonTabPrefs' isLiveAuthoritative for why.
            isReady: mastodonListsSuccess && mastodonTagsSuccess,
        }
    }

    const allFeeds = query.data ?? []

    // feeds for Home dropdown: always FOLLOWING_FEED first,
    // then pinned custom feeds (type preserved so useMixedTimeline can route list feeds correctly)
    const pinnedCustom = allFeeds
        .filter(f => f.pinned && f.type !== 'timeline')
        .map(f => ({ uri: f.uri, displayName: f.displayName, avatar: f.avatar, type: f.type }))
    const feeds: ResolvedFeed[] = [FOLLOWING_FEED, ...pinnedCustom]

    return {
        feeds,
        allFeeds,
        isLoading: query.isLoading,
        isReady: query.isSuccess,
    }
}
